import { supabase } from '../supabase/supabaseClient';

/**
 * User Service
 * Handles user registration, login, and profile management
 * Works with the 'user' table in Supabase
 */
class UserService {
  constructor() {
    this.isEnabled = this.checkSupabaseConfig();
    
    if (this.isEnabled) {
      console.log('👤 User Service initialized and ready');
    } else {
      console.warn('⚠️ User Service disabled - Supabase not configured');
    }
  }

  /**
   * Check if Supabase is properly configured
   */
  checkSupabaseConfig() {
    try {
      return !!(process.env.REACT_APP_SUPABASE_URL && process.env.REACT_APP_SUPABASE_KEY);
    } catch (error) {
      console.warn('User Service: Supabase config check failed', error);
      return false;
    }
  }

  /**
   * Register a new user
   */
  async registerUser(name, password, userType = 'patient', email = null) {
    if (!this.isEnabled) {
      // Fallback to localStorage if Supabase not configured
      return this.registerUserLocalStorage(name, password, userType, email);
    }

    try {
      // First register the user without email (since the RPC doesn't support it)
      const { data, error } = await supabase.rpc('register_user', {
        user_name: name,
        user_password: password,
        user_type: userType
      });

      if (error) {
        if (error.message.includes('already exists')) {
          throw new Error('A user with this name already exists');
        }
        throw error;
      }

      // If email is provided and registration successful, update the user record with email
      if (email && data) {
        try {
          const { error: updateError } = await supabase
            .from('user')
            .update({ email: email.toLowerCase() })
            .eq('id', data);

          if (updateError) {
            console.warn('Could not update email:', updateError);
            // Don't fail the registration if email update fails
          }
        } catch (err) {
          console.warn('Email update failed:', err);
        }
      }

      console.log('👤 User registered successfully:', name);
      return { success: true, userId: data };

    } catch (error) {
      console.error('User Service: Registration failed', error);
      throw new Error(error.message || 'Registration failed');
    }
  }

  /**
   * Login user (for access code or username-based login)
   */
  async loginUser(name, password) {
    if (!this.isEnabled) {
      // Fallback to localStorage if Supabase not configured
      return this.loginUserLocalStorage(name, password);
    }

    try {
      // Check if this is a patient access code (6 digits)
      const isPatientCode = /^\d{6}$/.test(name);

      if (isPatientCode) {
        console.log('🏥 Attempting patient login with access code:', name);
      }

      const { data, error } = await supabase.rpc('verify_user_login', {
        user_name: name,
        user_password: password
      });

      if (error) throw error;

      if (!data || data.length === 0) {
        // For patient codes, provide a more specific error
        if (isPatientCode) {
          throw new Error('Invalid or deactivated access code');
        } else {
          throw new Error('Invalid name or password');
        }
      }

      const user = data[0];

      // Only update last login for non-patient users
      // (patients don't have entries in the user table)
      if (user.user_type !== 'patient') {
        await this.updateLastLogin(user.user_id);
      }

      console.log('👤 User logged in successfully:', isPatientCode ? 'Patient' : name);
      return {
        success: true,
        user: {
          id: user.user_id,
          name: user.name,
          userType: user.user_type,
          user_type: user.user_type,
          role: user.role
        }
      };

    } catch (error) {
      console.error('User Service: Login failed', error);
      throw new Error(error.message || 'Login failed');
    }
  }

  /**
   * Login user with email
   */
  async loginUserWithEmail(email, password) {
    if (!this.isEnabled) {
      // Fallback to localStorage if Supabase not configured
      return this.loginUserWithEmailLocalStorage(email, password);
    }

    try {
      // First, try to find user by email in the user table
      const { data: userData, error: userError } = await supabase
        .from('user')
        .select('*')
        .eq('email', email.toLowerCase())
        .single();

      if (userError || !userData) {
        throw new Error('Invalid email or password');
      }

      // Then verify the password
      const { data, error } = await supabase.rpc('verify_user_login', {
        user_name: userData.name,
        user_password: password
      });

      if (error) throw error;

      if (!data || data.length === 0) {
        throw new Error('Invalid email or password');
      }

      const user = data[0];

      // Update last login
      await this.updateLastLogin(user.user_id);

      console.log('👤 User logged in successfully with email:', email);
      return {
        success: true,
        user: {
          id: user.user_id,
          name: user.name,
          email: email,
          userType: user.user_type,
          user_type: user.user_type,
          role: user.role
        }
      };

    } catch (error) {
      console.error('User Service: Email login failed', error);
      throw new Error('Invalid email or password');
    }
  }

  /**
   * Update last login timestamp
   */
  async updateLastLogin(userId) {
    if (!this.isEnabled) return;

    try {
      const { error } = await supabase
        .from('user')
        .update({ last_login: new Date().toISOString() })
        .eq('id', userId);

      if (error) throw error;

    } catch (error) {
      console.error('User Service: Failed to update last login', error);
      // Don't throw - this is not critical
    }
  }

  /**
   * Get user profile
   */
  async getUserProfile(userId) {
    if (!this.isEnabled) return null;

    try {
      const { data, error } = await supabase
        .from('user')
        .select('id, name, user_type, created_at, last_login')
        .eq('id', userId)
        .single();

      if (error) throw error;

      return data;

    } catch (error) {
      console.error('User Service: Failed to get profile', error);
      return null;
    }
  }

  /**
   * Check if user exists
   */
  async userExists(name) {
    if (!this.isEnabled) {
      return this.userExistsLocalStorage(name);
    }

    try {
      const { data, error } = await supabase
        .from('user')
        .select('id')
        .eq('name', name)
        .single();

      return !error && data;

    } catch (error) {
      return false;
    }
  }

  // Fallback methods for localStorage (when Supabase not configured)

  registerUserLocalStorage(name, password, userType, email = null) {
    const users = JSON.parse(localStorage.getItem('users') || '[]');

    if (users.some(user => user.name.toLowerCase() === name.toLowerCase())) {
      throw new Error('A user with this name already exists');
    }

    if (email && users.some(user => user.email && user.email.toLowerCase() === email.toLowerCase())) {
      throw new Error('A user with this email already exists');
    }

    const newUser = {
      id: Date.now().toString(),
      name,
      email,
      password, // In real app, this would be hashed
      userType,
      createdAt: new Date().toISOString()
    };

    users.push(newUser);
    localStorage.setItem('users', JSON.stringify(users));

    console.log('👤 User registered in localStorage:', name);
    return { success: true, userId: newUser.id };
  }

  loginUserLocalStorage(name, password) {
    // Check if this is a patient access code (6 digits)
    const isPatientCode = /^\d{6}$/.test(name);

    if (isPatientCode) {
      // For testing: accept specific access codes
      const validCodes = ['123456', '654321', '847392', '923847'];
      if (validCodes.includes(name)) {
        console.log('👤 Patient logged in from localStorage with access code');
        return {
          success: true,
          user: {
            id: 'patient_' + name,
            name: 'Test Patient',
            userType: 'patient',
            role: 'patient'
          }
        };
      } else {
        throw new Error('Invalid or deactivated access code');
      }
    }

    // Regular user login
    const users = JSON.parse(localStorage.getItem('users') || '[]');
    const user = users.find(u =>
      u.name.toLowerCase() === name.toLowerCase() && u.password === password
    );

    if (!user) {
      throw new Error('Invalid name or password');
    }

    console.log('👤 User logged in from localStorage:', name);
    return {
      success: true,
      user: {
        id: user.id,
        name: user.name,
        userType: user.userType || 'patient',
        role: user.userType || 'patient'
      }
    };
  }

  loginUserWithEmailLocalStorage(email, password) {
    const users = JSON.parse(localStorage.getItem('users') || '[]');
    const user = users.find(u =>
      u.email && u.email.toLowerCase() === email.toLowerCase() && u.password === password
    );

    if (!user) {
      throw new Error('Invalid email or password');
    }

    console.log('👤 User logged in from localStorage with email:', email);
    return {
      success: true,
      user: {
        id: user.id,
        name: user.name,
        email: user.email,
        userType: user.userType || 'clinician'
      }
    };
  }

  userExistsLocalStorage(name) {
    const users = JSON.parse(localStorage.getItem('users') || '[]');
    return users.some(user => user.name.toLowerCase() === name.toLowerCase());
  }

  /**
   * Get users by type (for SuperAdmin dashboard)
   */
  async getUsersByType(userType) {
    if (!this.isEnabled) {
      console.warn('UserService: Supabase not configured');
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('user')
        .select('*')
        .eq('user_type', userType)
        .order('created_at', { ascending: false });

      if (error) throw error;

      console.log(`👤 Loaded ${data?.length || 0} users of type: ${userType}`);
      return data || [];

    } catch (error) {
      console.error('User Service: Failed to get users by type', error);
      return [];
    }
  }

  /**
   * Check if service is enabled
   */
  isServiceEnabled() {
    return this.isEnabled;
  }
}

// Create singleton instance
const userService = new UserService();

export default userService; 